import {
  ApiError,
  type Announcement,
  type AnnouncementSummary,
  type PortalRole,
  type PublishAnnouncementResponse,
} from '@central/api';
import { Card, PageHeader, StatusChip, color, formatDate } from '@central/ui';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Group,
  Modal,
  MultiSelect,
  Radio,
  Select,
  Table,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import {
  allRoles,
  buildAudience,
  buildPatch,
  buildRequest,
  dayFromEndsAt,
  describeAudience,
  draftFrom,
  emptyDraft,
  endsAtFromDay,
  fieldErrors,
  kindChip,
  kindOptions,
  peopleWord,
  publishAtFrom,
  releaseLine,
  roleLabel,
  validate,
  type Draft,
  type DraftErrors,
} from '../lib/announce';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';

export interface AnnouncementsSearch {
  /** A draft to open in the composer. */
  id?: string;
}

/** Announcements (docs/design/AdminAnnounce.dc.html): composer for writers, recent list for all staff. */
export function Announcements() {
  const api = useApi();
  const canWrite = useCanWrite();
  const navigate = useNavigate({ from: '/announcements' });
  const { id } = useSearch({ from: '/app/announcements' });
  const [published, setPublished] = useState<PublishAnnouncementResponse>();
  const [fresh, setFresh] = useState(0);
  const [opened, setOpened] = useState<AnnouncementSummary>();

  const loaded = useQuery({
    queryKey: keys.announcement(id ?? ''),
    queryFn: () => api.announcements.get(id!),
    enabled: canWrite && !!id,
  });

  const startNew = () => {
    setFresh((n) => n + 1);
    void navigate({ search: {} });
  };

  return (
    <>
      <PageHeader
        title={canWrite ? (id ? 'Edit announcement' : 'New announcement') : 'Announcements'}
        subtitle="Publishing a release in Releases starts a draft here for you"
        actions={
          canWrite &&
          id && (
            <Button variant="default" size="sm" h={40} onClick={startNew}>
              New announcement
            </Button>
          )
        }
      />
      {published && (
        <Alert color="green" withCloseButton onClose={() => setPublished(undefined)}>
          {publishedText(published)}
        </Alert>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
        {canWrite && (
          <div style={{ flex: '3 1 440px', minWidth: 0 }}>
            {id ? (
              <QueryState isPending={loaded.isPending} error={loaded.error}>
                {() =>
                  loaded.data!.status === 'draft' ? (
                    <Composer
                      key={id}
                      initial={loaded.data}
                      onPublished={(r) => {
                        setPublished(r);
                        startNew();
                      }}
                      onDeleted={startNew}
                    />
                  ) : (
                    <Alert color="yellow">
                      This announcement is already published. Open it from Recent to see who has
                      seen it.
                    </Alert>
                  )
                }
              </QueryState>
            ) : (
              <Composer
                key={`new-${fresh}`}
                onPublished={(r) => {
                  setPublished(r);
                  setFresh((n) => n + 1);
                }}
                onDeleted={startNew}
              />
            )}
          </div>
        )}
        <div style={{ flex: '2 1 300px', minWidth: 0 }}>
          <Recent
            onOpen={(a) =>
              a.status === 'draft' && canWrite
                ? void navigate({ search: { id: a.id } })
                : setOpened(a)
            }
          />
        </div>
      </div>
      {opened && <ReceiptsModal a={opened} onClose={() => setOpened(undefined)} />}
    </>
  );
}

function publishedText(r: PublishAnnouncementResponse): string {
  const reach = `${r.customers} customer${r.customers === 1 ? '' : 's'}, ${r.people} ${r.people === 1 ? 'person' : 'people'}`;
  const emails = r.emails ? ` ${r.emails} email${r.emails === 1 ? '' : 's'} queued.` : '';
  const head =
    r.announcement.status === 'scheduled'
      ? `Scheduled for ${formatDate(r.announcement.publishAt)}: ${reach}.`
      : `Published to ${reach}.`;
  return head + emails;
}

function Composer({
  initial,
  onPublished,
  onDeleted,
}: {
  initial?: Announcement;
  onPublished: (r: PublishAnnouncementResponse) => void;
  onDeleted: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => (initial ? draftFrom(initial) : emptyDraft()));
  const [savedId, setSavedId] = useState(initial?.id);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [saved, setSaved] = useState(false);
  const [names, setNames] = useState<Record<string, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerQuery] = useDebouncedValue(customerSearch.trim(), 250);

  const set = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  const releases = useQuery({ queryKey: keys.releases, queryFn: () => api.releases.list() });
  const customers = useQuery({
    queryKey: keys.customerList(customerQuery, ''),
    queryFn: () => api.customers.list({ query: customerQuery || undefined }),
    enabled: draft.audienceType === 'customers',
  });

  // Live reach for the audience as it stands; a count is shown only for the
  // audience currently on screen, never a stale one.
  const audience = buildAudience(draft);
  const audienceKey = JSON.stringify(audience);
  const [settledKey] = useDebouncedValue(audienceKey, 300);
  const audienceErrors = Object.keys(validate(draft, 'save', new Date())).some((k) =>
    k.startsWith('audience.'),
  );
  const preview = useQuery({
    queryKey: keys.audiencePreview(settledKey),
    queryFn: () => api.announcements.preview({ audience: JSON.parse(settledKey) }),
    enabled: !audienceErrors && settledKey === audienceKey,
  });
  const reach = settledKey === audienceKey && !audienceErrors ? preview.data : undefined;

  const persist = async (): Promise<Announcement> => {
    const a = savedId
      ? await api.announcements.update(savedId, buildPatch(draft))
      : await api.announcements.create(buildRequest(draft));
    setSavedId(a.id);
    return a;
  };

  const onError = (e: Error) => {
    if (e instanceof ApiError && e.fields) setErrors(fieldErrors(e.fields));
  };

  const save = useMutation({
    mutationFn: persist,
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: keys.announcements });
    },
    onError,
  });

  const publish = useMutation({
    mutationFn: async () => {
      const a = await persist();
      return api.announcements.publish(
        a.id,
        draft.publishMode === 'later' ? { publishAt: publishAtFrom(draft.publishAt) } : {},
      );
    },
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: keys.announcements });
      onPublished(r);
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: () => api.announcements.delete(savedId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.announcements });
      onDeleted();
    },
  });

  const check = (action: 'save' | 'publish') => {
    const v = validate(draft, action, new Date());
    setErrors(v);
    return Object.keys(v).length === 0;
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (check('publish')) publish.mutate();
  };

  const busy = save.isPending || publish.isPending || remove.isPending;
  const failure = save.error ?? publish.error ?? remove.error;
  const unmapped =
    failure && !(failure instanceof ApiError && Object.keys(fieldErrors(failure.fields)).length);
  const count = reach ? ` ${reach.customers} customer${reach.customers === 1 ? '' : 's'}` : '';
  const publishLabel =
    draft.publishMode === 'later'
      ? `Schedule${count && ` for${count}`}`
      : `Publish${count && ` to${count}`}`;
  const reachNote = (type: Draft['audienceType']) =>
    draft.audienceType === type && reach ? (
      <span style={muted}>
        {' '}
        · {reach.customers} customer{reach.customers === 1 ? '' : 's'}
      </span>
    ) : null;

  const customerOptions = [
    ...(customers.data?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
    ...draft.customerIds
      .filter((cid) => !customers.data?.items.some((c) => c.id === cid))
      .map((cid) => ({ value: cid, label: names[cid] ?? cid })),
  ];

  return (
    <Card>
      <form
        onSubmit={submit}
        noValidate
        aria-label="Announcement"
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <Grid>
          <Select
            label="Kind"
            data={kindOptions}
            value={draft.kind}
            allowDeselect={false}
            onChange={(v) => v && set({ kind: v as Draft['kind'] })}
          />
          {draft.kind === 'release' && (
            <Select
              label="Release"
              placeholder="Choose a release"
              data={(releases.data?.items ?? []).map((r) => r.version)}
              value={draft.releaseVersion || null}
              error={errors.releaseVersion}
              onChange={(v) =>
                set({
                  releaseVersion: v ?? '',
                  ...(v && !draft.maintenanceCovers ? { maintenanceCovers: releaseLine(v) } : {}),
                })
              }
            />
          )}
        </Grid>
        <TextInput
          label="Title"
          value={draft.title}
          error={errors.title}
          onChange={(e) => set({ title: e.currentTarget.value })}
        />
        <Textarea
          label="Message"
          description="Plain text; a blank line starts a new paragraph."
          autosize
          minRows={5}
          value={draft.body}
          error={errors.body}
          onChange={(e) => set({ body: e.currentTarget.value })}
        />

        <Radio.Group
          label="Who gets it"
          value={draft.audienceType}
          onChange={(v) => set({ audienceType: v as Draft['audienceType'] })}
          error={
            errors['audience.customerIds'] ??
            errors['audience.versionBelow'] ??
            errors['audience.maintenanceCovers']
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
            <Radio value="all" label={<>All customers{reachNote('all')}</>} />
            <Radio
              value="maintenance_covers"
              label={
                <>
                  Customers whose maintenance covers{' '}
                  {draft.audienceType === 'maintenance_covers'
                    ? 'the line below'
                    : 'a release line'}
                  {reachNote('maintenance_covers')}
                </>
              }
            />
            {draft.audienceType === 'maintenance_covers' && (
              <Indented>
                <TextInput
                  aria-label="Release line"
                  placeholder="1.1"
                  w={140}
                  value={draft.maintenanceCovers}
                  onChange={(e) => set({ maintenanceCovers: e.currentTarget.value })}
                />
              </Indented>
            )}
            <Radio
              value="version_below"
              label={<>Customers running a version before{reachNote('version_below')}</>}
            />
            {draft.audienceType === 'version_below' && (
              <Indented>
                <TextInput
                  aria-label="Running a version before"
                  placeholder="1.0.3"
                  w={140}
                  value={draft.versionBelow}
                  onChange={(e) => set({ versionBelow: e.currentTarget.value })}
                />
              </Indented>
            )}
            <Radio value="customers" label={<>Selected customers{reachNote('customers')}</>} />
            {draft.audienceType === 'customers' && (
              <Indented>
                <MultiSelect
                  aria-label="Customers"
                  placeholder="Search customers"
                  searchable
                  searchValue={customerSearch}
                  onSearchChange={setCustomerSearch}
                  filter={({ options }) => options}
                  data={customerOptions}
                  value={draft.customerIds}
                  onChange={(ids) => {
                    const known = Object.fromEntries(
                      customerOptions.map((o) => [o.value, o.label]),
                    );
                    setNames((n) => ({ ...n, ...known }));
                    set({ customerIds: ids });
                  }}
                />
              </Indented>
            )}
          </div>
        </Radio.Group>
        <Indented>
          <Chip.Group
            multiple
            value={draft.roles}
            onChange={(v) => v.length && set({ roles: v as PortalRole[] })}
          >
            <Group gap={6}>
              {allRoles.map((r) => (
                <Chip key={r} value={r} size="sm" radius="sm">
                  {roleLabel[r]}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Indented>

        <Checkbox.Group label="Where it shows" error={errors.channels}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
            {(
              [
                ['popup', 'Pop-up at next sign-in, once per person'],
                ['bell', 'Bell notifications in the portal'],
                ['email', 'Email to owners'],
                ['banner', 'Banner on every page until it ends'],
              ] as const
            ).map(([k, label]) => (
              <Checkbox
                key={k}
                label={label}
                checked={draft.channels[k]}
                onChange={(e) =>
                  set({ channels: { ...draft.channels, [k]: e.currentTarget.checked } })
                }
              />
            ))}
          </div>
        </Checkbox.Group>

        <Grid>
          <Radio.Group
            label="Publish"
            value={draft.publishMode}
            onChange={(v) => set({ publishMode: v as Draft['publishMode'] })}
          >
            <Group gap={16} pt={8}>
              <Radio value="now" label="Now" />
              <Radio value="later" label="Later" />
            </Group>
          </Radio.Group>
          <TextInput
            label="Stop showing after"
            type="date"
            value={draft.endsOn}
            error={errors.endsAt}
            onChange={(e) => set({ endsOn: e.currentTarget.value })}
          />
        </Grid>
        {draft.publishMode === 'later' && (
          <TextInput
            label="Publish at (UTC)"
            type="datetime-local"
            w={260}
            maw="100%"
            value={draft.publishAt}
            error={errors.publishAt}
            onChange={(e) => set({ publishAt: e.currentTarget.value })}
          />
        )}

        {unmapped && <Alert color="red">{errorMessage(failure)}</Alert>}
        {saved && <Alert color="green">Draft saved.</Alert>}
        <Group justify="flex-end" gap={10}>
          {savedId && (
            <Button
              variant="subtle"
              color="red"
              disabled={busy}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete draft
            </Button>
          )}
          <Button
            variant="default"
            disabled={busy}
            loading={save.isPending}
            onClick={() => check('save') && save.mutate()}
          >
            Save draft
          </Button>
          <Button type="submit" color="dark" disabled={busy} loading={publish.isPending}>
            {publishLabel}
          </Button>
        </Group>
      </form>
    </Card>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14,
      }}
    >
      {children}
    </div>
  );
}

function Indented({ children }: { children: ReactNode }) {
  return <div style={{ paddingLeft: 30 }}>{children}</div>;
}

function audienceLine(a: AnnouncementSummary): string {
  const aud = describeAudience(a.audience);
  switch (a.status) {
    case 'draft':
      return `Draft · ${aud}`;
    case 'scheduled':
      return `${aud} · scheduled ${formatDate(a.publishAt)}`;
    case 'live':
      return `${aud} · ${formatDate(a.publishAt)}`;
    case 'ended':
      return `${aud} · ${formatDate(a.publishAt)} · ended`;
  }
}

function seenLine(a: AnnouncementSummary): string {
  if (a.status === 'draft') return 'Not published';
  return `Seen by ${a.receipts.seen} of ${a.receipts.audience} ${peopleWord(a.audience)}`;
}

function Recent({ onOpen }: { onOpen: (a: AnnouncementSummary) => void }) {
  const api = useApi();
  const list = useQuery({
    queryKey: keys.announcementList,
    queryFn: () => api.announcements.list(),
  });
  return (
    <div role="region" aria-label="Recent">
      <Card title="Recent">
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No announcements yet.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {list.data!.items.map((a) => {
                  const chip = kindChip[a.kind];
                  return (
                    <li key={a.id} style={{ borderBottom: `1px solid ${color.rowRule}` }}>
                      <UnstyledButton
                        onClick={() => onOpen(a)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          width: '100%',
                          padding: '12px 0',
                        }}
                      >
                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</span>
                          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                        </span>
                        <span style={{ fontSize: 13, ...muted }}>{audienceLine(a)}</span>
                        <span style={{ fontSize: 13, color: color.textBody }}>{seenLine(a)}</span>
                      </UnstyledButton>
                    </li>
                  );
                })}
              </ul>
            )
          }
        </QueryState>
      </Card>
    </div>
  );
}

function ReceiptsModal({ a, onClose }: { a: AnnouncementSummary; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [endsOn, setEndsOn] = useState(dayFromEndsAt(a.endsAt));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const receipts = useQuery({
    queryKey: keys.announcementReceipts(a.id),
    queryFn: () => api.announcements.receipts(a.id),
  });
  const end = useMutation({
    mutationFn: () => api.announcements.update(a.id, { endsAt: endsAtFromDay(endsOn) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.announcements }),
  });
  const remove = useMutation({
    mutationFn: () => api.announcements.delete(a.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.announcements });
      onClose();
    },
  });
  const word = peopleWord(a.audience);
  const chip = kindChip[a.kind];

  return (
    <Modal opened onClose={onClose} title={a.title} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Group gap={8}>
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
          <span style={{ fontSize: 13, ...muted }}>{audienceLine(a)}</span>
        </Group>
        <QueryState isPending={receipts.isPending} error={receipts.error}>
          {() => {
            const r = receipts.data!;
            return (
              <>
                <div style={{ fontSize: 14 }}>
                  Seen by {r.seen} of {r.audience} {word}
                </div>
                {r.customers.length === 0 ? (
                  <Empty>No one is reached yet.</Empty>
                ) : (
                  <DataTable label="Receipts" minWidth={360} head={['Customer', 'Seen']}>
                    {r.customers.map((c) => (
                      <Table.Tr key={c.customerId}>
                        <Table.Td>{c.customerName}</Table.Td>
                        <Table.Td>
                          {c.seen} of {c.audience}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </DataTable>
                )}
              </>
            );
          }}
        </QueryState>
        {canWrite && (
          <Group align="flex-end" gap={10}>
            <TextInput
              label="Stop showing after"
              type="date"
              value={endsOn}
              onChange={(e) => setEndsOn(e.currentTarget.value)}
            />
            <Button
              variant="default"
              disabled={!endsOn}
              loading={end.isPending}
              onClick={() => end.mutate()}
            >
              Save end date
            </Button>
          </Group>
        )}
        {end.isSuccess && <Alert color="green">End date saved.</Alert>}
        {canWrite && a.status === 'scheduled' && (
          <Group gap={10}>
            {confirmDelete ? (
              <>
                <span style={{ fontSize: 14 }}>Delete it and its unsent emails?</span>
                <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Delete
                </Button>
                <Button variant="default" onClick={() => setConfirmDelete(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button variant="subtle" color="red" onClick={() => setConfirmDelete(true)}>
                Delete scheduled announcement
              </Button>
            )}
          </Group>
        )}
        {(end.error ?? remove.error) && (
          <Alert color="red">{errorMessage(end.error ?? remove.error)}</Alert>
        )}
      </div>
    </Modal>
  );
}
